// src/lib/materialTemplates.mjs
// ───────────────────────────────────────────────────────────────────────────
// 🧱 Meta-modelo del catálogo de materia prima (PLAN-CATALOGO-MATERIALES §3 · Slice 1)
//
// Atributos dirigidos por CATEGORÍA, config-EN-DATOS: cada familia define su ficha
// técnica (atributos + validación) → el form se renderiza dinámicamente y agregar
// una familia es CREAR un registro, no programar. Este módulo es la **fuente de
// verdad** que el seed (`scripts/seed-material-templates.mjs`) escribe a Firestore
// (`materialTemplates/{category}`) y que el validador puro consume.
//
// PURO y .mjs a propósito: lo importan el seed (Node), los unit tests (Jest) y, en
// los siguientes slices, el generador `generateDisplayName`/`generateSpecCode` y el
// form `MaterialModal` (CRA). "Reusar, no duplicar".
//
// Términos de negocio en INGLÉS. Los labels humanos NO viven aquí: cada atributo
// lleva `labelKey` → `t(labelKey)` (bilingüe es/en). Tenant-scoping: hoy single-tenant
// (colección raíz `materialTemplates`); cuando exista multi-tenant, filtrar por tenant.
//
// ⚠️ 6 FAMILIAS (§3.2, mapeo aprobado 2026-06-13; SUPERSEDE el borrador de 8). Decisión
//    D-1/D-2 CERRADA por Daniel: **la marca es un ATRIBUTO, no una familia** — HONEYCOMB
//    es una sola familia con `brand` [FALCONBOARD/XANITA/RE-BOARD/SWEDBOARD]; FLUTED_PLASTIC
//    con `brand` [COROPLAST]. `thickness` es campo COMÚN numérico (mm) con rango por familia
//    (`validation.thickness`), no enum. Clave de categoría `FLUTED_PLASTIC` (sin espacio,
//    doc-id/identificador limpio); su nombre humano sale de `t('matCat.FLUTED_PLASTIC')`.
// ───────────────────────────────────────────────────────────────────────────

// Presentaciones y unidades comunes (§3.3 — viven en el material, no en la plantilla).
export const PRESENTATIONS = ['SHEET', 'ROLL'];
export const DIMENSION_UNIT = 'in';     // MVP: pulgadas fijo (decisión ya tomada).
export const THICKNESS_UNIT = 'mm';     // unidad canónica de espesor (§3.3).

// #2 (patch) — Espesor en mm DERIVADO de la fluta del corrugado (atributo categórico).
// El servidor lo setea; el cliente NO lo ingresa. 'A' no se usa en Cartonbar.
export const FLUTE_THICKNESS_MM = { B: 3, E: 1.5, C: 4, EB: 5, BC: 7, N: 0.46 };
// #4 (patch) — Caliper (PT) → mm SOLO como conversión on-demand para costeo. NUNCA se
// persiste: en PAPERBOARD el espesor ES el caliper en PT (excepción, no mm).
export const CALIPER_PT_TO_MM = 0.0254;

/** Espesor (mm) derivado de la fluta de un corrugado; null si la fluta no está mapeada. */
export function deriveCorrugatedThicknessMm(attributes) {
  const f = attributes && attributes.flute;
  return Object.prototype.hasOwnProperty.call(FLUTE_THICKNESS_MM, f) ? FLUTE_THICKNESS_MM[f] : null;
}

// Campos COMUNES a todo material (§3.3). El form los pinta para toda familia.
// `thickness` es común (rango por familia en `validation.thickness`). `brand` es común
// (texto libre) SALVO en las familias que lo definen como enum discriminante
// (HONEYCOMB/RIGID/FLUTED_PLASTIC); ahí la plantilla manda y el form usa el enum (Slice 4).
export const COMMON_FIELDS = [
  { field: 'presentation', labelKey: 'matAttr.presentation', type: 'enum', options: PRESENTATIONS, required: true },
  { field: 'dimensions', labelKey: 'matAttr.dimensions', type: 'dimensions', unit: DIMENSION_UNIT, required: true },
  { field: 'thickness', labelKey: 'matAttr.thickness', type: 'thickness', unit: THICKNESS_UNIT, required: false },
  { field: 'brand', labelKey: 'matAttr.brand', type: 'text', required: false },
  { field: 'supplier', labelKey: 'matAttr.supplier', type: 'text', required: false },
  { field: 'price', labelKey: 'matAttr.price', type: 'number', required: false }, // null en MVP (costeo → FreshBooks)
  { field: 'imageUrl', labelKey: 'matAttr.imageUrl', type: 'image', required: false },
];

// Plantillas por familia (§3.2). `specOrder` rige el orden en displayName/specCode (Slice 2).
// `validation.thickness {min,max,unit}` guarda el campo común numérico `thickness` en mm
// (rechaza p. ej. "1.8 in" en un acrílico: unidad ≠ mm → error 'unit').
export const MATERIAL_TEMPLATES = {
  CORRUGATED: {
    category: 'CORRUGATED',
    specPrefix: 'COR',
    hasGrain: true, // activa el rótulo de flauta (alinea con la regla m1 de retales)
    thicknessDisplay: 'none', // EXENTO: la flauta ya codifica el grosor (no va en code/displayName)
    attributes: [
      { field: 'flute', labelKey: 'matAttr.flute', type: 'enum', options: ['B', 'E', 'BC', 'EB', 'C', 'N'], required: true, specOrder: 1 },
      { field: 'test', labelKey: 'matAttr.test', type: 'enum', options: ['200#', '275#', '350#', '375#', '32ECT', '44ECT', 'UNSPEC'], required: false, specOrder: 2 },
      // ULTRAWHITE = liner premium (ortogonal a la flauta; aplica E, N, etc.).
      // 3 CARAS (B1): exterior / MÉDIUM (centro, el corrugating medium, uniforme en TODO flute) /
      // interior — las tres requeridas, mismo enum, specOrder 3 → grupo EXT/CENTRO/INT (paridad honeycomb).
      { field: 'outerLiner', labelKey: 'matAttr.outerLiner', type: 'enum', options: ['KRAFT', 'WHITE', 'KEMI', 'ULTRAWHITE'], required: true, specOrder: 3 },
      { field: 'medium', labelKey: 'matAttr.medium', type: 'enum', options: ['KRAFT', 'WHITE', 'KEMI', 'ULTRAWHITE'], required: true, specOrder: 3 },
      { field: 'innerLiner', labelKey: 'matAttr.innerLiner', type: 'enum', options: ['KRAFT', 'WHITE', 'KEMI', 'ULTRAWHITE'], required: true, specOrder: 3 },
    ],
    validation: { thickness: { min: 1, max: 8, unit: 'mm' } },
  },
  HONEYCOMB: {
    category: 'HONEYCOMB',
    specPrefix: 'HCB',
    thicknessRequired: true, // D-S2: el grosor define el SKU del panel
    hasGrain: false,
    thicknessDisplay: 'mm',
    // Bloque reusable "sandwich-faces": outer/core/inner, las TRES requeridas.
    // coreFace = papel del panal (el del medio). foam/Ultraboard lo reusará después.
    attributes: [
      { field: 'brand', labelKey: 'matAttr.brand', type: 'enum', options: ['FALCONBOARD', 'XANITA', 'RE-BOARD', 'SWEDBOARD'], required: true, specOrder: 1 },
      { field: 'outerFace', labelKey: 'matAttr.outerFace', type: 'enum', options: ['WHITE', 'KRAFT', 'KEMI'], required: true, specOrder: 2 },
      { field: 'coreFace', labelKey: 'matAttr.coreFace', type: 'enum', options: ['WHITE', 'KRAFT', 'KEMI'], required: true, specOrder: 2 },
      { field: 'innerFace', labelKey: 'matAttr.innerFace', type: 'enum', options: ['WHITE', 'KRAFT', 'KEMI'], required: true, specOrder: 2 },
    ],
    validation: { thickness: { min: 5, max: 20, unit: 'mm' } },
  },
  RIGID: {
    category: 'RIGID', // PVC, acrylic, styrene, foam (Sintra/Gator/Dibond → marca)
    specPrefix: 'RIG',
    thicknessRequired: true, // D-S2: el grosor (fracción/mil) define el SKU rígido
    hasGrain: false,
    thicknessDisplay: 'fraction', // nativo en fracción de pulgada (styrene → mil, ver materialNaming)
    attributes: [
      { field: 'subtype', labelKey: 'matAttr.subtype', type: 'enum', options: ['PVC', 'ACRYLIC', 'STYRENE', 'FOAM'], required: true, specOrder: 1 },
      { field: 'color', labelKey: 'matAttr.color', type: 'enum', options: ['WHITE', 'BLACK', 'CLEAR'], required: true, specOrder: 2 },
      { field: 'brand', labelKey: 'matAttr.brand', type: 'enum', options: ['ULTRABOARD', 'DURAPRIME', 'GENERIC'], required: false, specOrder: 3 },
    ],
    validation: { thickness: { min: 0.5, max: 15, unit: 'mm' } },
  },
  FLUTED_PLASTIC: {
    category: 'FLUTED_PLASTIC', // Coroplast & similares (antes "CORPLAST")
    specPrefix: 'CPL',
    thicknessRequired: true, // D-S2: el grosor define el SKU del coroplast
    hasGrain: false,
    thicknessDisplay: 'mm',
    attributes: [
      { field: 'brand', labelKey: 'matAttr.brand', type: 'enum', options: ['COROPLAST'], required: true, specOrder: 1 },
      { field: 'color', labelKey: 'matAttr.color', type: 'enum', options: ['WHITE', 'BLACK'], required: true, specOrder: 2 },
    ],
    validation: { thickness: { min: 2, max: 12, unit: 'mm' } },
  },
  PAPERBOARD: {
    category: 'PAPERBOARD',
    specPrefix: 'PPB',
    hasGrain: false,
    thicknessDisplay: 'none', // EXENTO: el caliper (PT) ya codifica el grosor
    attributes: [
      { field: 'coating', labelKey: 'matAttr.coating', type: 'enum', options: ['C1S', 'C2S', 'SBS', 'CARTULINA'], required: true, specOrder: 1 },
      { field: 'caliper', labelKey: 'matAttr.caliper', type: 'number', unit: 'PT', required: true, specOrder: 2 },
      { field: 'color', labelKey: 'matAttr.color', type: 'enum', options: ['WHITE', 'NATURAL'], required: false, specOrder: 3 },
    ],
    validation: { thickness: { min: 0.2, max: 1.6, unit: 'mm' }, caliper: { min: 8, max: 60, unit: 'PT' } }, // cierre auditor: caliper ≈8–60 PT
  },
  WOOD: {
    category: 'WOOD', // plywood, MDF
    specPrefix: 'WOD',
    thicknessRequired: true, // D-S2: el grosor define el SKU de madera
    hasGrain: false, // opcional según material; MVP false
    thicknessDisplay: 'mm',
    attributes: [
      { field: 'type', labelKey: 'matAttr.type', type: 'enum', options: ['PLYWOOD', 'MDF'], required: true, specOrder: 1 },
      { field: 'finish', labelKey: 'matAttr.finish', type: 'enum', options: ['PREFINISHED', 'RAW'], required: true, specOrder: 2 },
    ],
    validation: { thickness: { min: 3, max: 25, unit: 'mm' } },
  },
};

/** Lista de categorías (claves de las plantillas). */
export function listCategories() {
  return Object.keys(MATERIAL_TEMPLATES);
}

/** Plantilla por categoría (null si no existe). */
export function getTemplate(category) {
  return MATERIAL_TEMPLATES[category] || null;
}

/** Convierte la plantilla a su doc Firestore `materialTemplates/{category}` (lo que siembra el seed). */
export function templateDoc(category) {
  const t = getTemplate(category);
  if (!t) return null;
  return {
    category: t.category,
    specPrefix: t.specPrefix,
    hasGrain: !!t.hasGrain,
    thicknessDisplay: t.thicknessDisplay || 'none',
    thicknessRequired: !!t.thicknessRequired,
    attributes: t.attributes,
    validation: t.validation || {},
    active: true, // cierre auditor: las plantillas nacen activas
  };
}

// Helpers numéricos para la validación de thickness (acepta {value,unit} o "4MM"/"1/8\"").
function parseThicknessValue(thickness) {
  if (thickness == null) return null;
  if (typeof thickness === 'object') {
    const v = Number(thickness.value);
    return Number.isFinite(v) ? { value: v, unit: thickness.unit || null } : null;
  }
  const s = String(thickness).trim();
  const m = s.match(/^([0-9]*\.?[0-9]+)\s*(mm|in|pt)?/i);
  if (!m) return null;
  return { value: Number(m[1]), unit: m[2] ? m[2].toLowerCase() : null };
}

/**
 * Valida los atributos de un material contra su plantilla (§5). PURO.
 * Devuelve un array de errores `{ field, rule }` (vacío = válido):
 *   - rule 'unknownCategory' → no hay plantilla para esa categoría
 *   - rule 'required'        → atributo requerido ausente/vacío
 *   - rule 'enum'            → valor fuera del enum permitido
 *   - rule 'range'           → thickness numérico fuera del rango de la familia
 *   - rule 'unit'            → thickness en una unidad distinta a la esperada (mm)
 * El UI mapea cada `{field, rule}` a un mensaje vía `t('matValid.<rule>')`.
 * @param {string|object} categoryOrTemplate
 * @param {object} material  { attributes:{...}, thickness?, presentation?, ... }
 * @return {Array<{field:string, rule:string}>}
 */
/**
 * #6 (patch) — Atributos enum con un valor GUARDADO fuera del catálogo (no en `options`).
 * El modal los muestra como "actual (fuera de catálogo)" y obliga a elegir uno válido
 * (nunca coerción silenciosa). PURO. Devuelve [{ field, value }].
 */
export function offEnumAttributes(categoryOrTemplate, attributes = {}) {
  const template = typeof categoryOrTemplate === 'string' ? getTemplate(categoryOrTemplate) : categoryOrTemplate;
  if (!template) return [];
  const out = [];
  for (const def of template.attributes || []) {
    if (def.type !== 'enum' || !Array.isArray(def.options)) continue;
    const v = attributes[def.field];
    if (v != null && String(v).trim() !== '' && !def.options.includes(v)) out.push({ field: def.field, value: v });
  }
  return out;
}

export function validateMaterialAttributes(categoryOrTemplate, material = {}) {
  const template = typeof categoryOrTemplate === 'string' ? getTemplate(categoryOrTemplate) : categoryOrTemplate;
  if (!template) return [{ field: 'category', rule: 'unknownCategory' }];

  const errors = [];
  const attrs = material.attributes || {};

  for (const def of template.attributes || []) {
    const value = attrs[def.field];
    const empty = value === undefined || value === null || String(value).trim() === '';
    if (def.required && empty) { errors.push({ field: def.field, rule: 'required' }); continue; }
    if (empty) continue;
    if (def.type === 'enum' && Array.isArray(def.options) && !def.options.includes(value)) {
      errors.push({ field: def.field, rule: 'enum' });
    }
    if (def.type === 'number' && !Number.isFinite(Number(value))) {
      errors.push({ field: def.field, rule: 'numeric' }); // caliper, etc. deben ser numéricos
    }
  }

  // Espesor REQUERIDO por familia (D-S2): RIGID/HONEYCOMB/WOOD/FLUTED_PLASTIC exigen
  // thickness; CORRUGATED/PAPERBOARD están exentos (flauta/caliper codifican el grosor).
  if (template.thicknessRequired) {
    const th = material.thickness;
    const thEmpty = th == null || th === ''
      || (typeof th === 'object' && (th.value == null || th.value === '' || !Number.isFinite(Number(th.value))));
    if (thEmpty) errors.push({ field: 'thickness', rule: 'required' });
  }

  // Rango de thickness del campo común numérico (cuando la plantilla lo define).
  const range = template.validation && template.validation.thickness;
  if (range && material.thickness != null && material.thickness !== '') {
    const parsed = parseThicknessValue(material.thickness);
    if (parsed) {
      if (parsed.unit && range.unit && parsed.unit !== range.unit) {
        errors.push({ field: 'thickness', rule: 'unit' });
      } else if (parsed.value < range.min || parsed.value > range.max) {
        errors.push({ field: 'thickness', rule: 'range' });
      }
    }
  }

  // Rangos numéricos de ATRIBUTOS (p. ej. caliper 8–60 PT en PAPERBOARD). `thickness`
  // ya se trató arriba sobre el campo común.
  for (const [field, r] of Object.entries(template.validation || {})) {
    if (field === 'thickness') continue;
    const raw = attrs[field];
    if (raw == null || String(raw).trim() === '') continue;
    const num = Number(raw);
    if (Number.isFinite(num) && (num < r.min || num > r.max)) errors.push({ field, rule: 'range' });
  }

  // Presentación común (si viene, debe ser válida).
  if (material.presentation && !PRESENTATIONS.includes(material.presentation)) {
    errors.push({ field: 'presentation', rule: 'enum' });
  }

  return errors;
}
